import { Document, DocTypes, DocsFilter } from './document';
import type { Cube } from './cube';
import type { Localization } from './localization';
import type { homee } from '../typings/homee';
import { I18n } from '@aws-amplify/core';
import { Translations } from '../plugins/i18n';
import { FieldTypes, DetailData } from '../typings/field';
import { persistentDocumentsStore, userStore } from '../plugins/store';
import type { MyDataTableHeader } from '../typings';
import type { ListFilter, Rows } from '.';
import _ from 'lodash';

export interface Protocol extends Document {
  _docType: DocTypes.Protocol;
  name: string;
  number: homee.CANodeProtocol;
  displayName: Localization | null;
  cubes: Cube[];
}

export const defaultListProperties: string = `
  name
  number
  displayName {
    _id
  }
  cubes {
    _id
  }
`;

export const defaultDetailProperties: string = `
  name
  number
  displayName {
    _id
  }
  cubes {
    _id
  }
`;

export function getColumns(): MyDataTableHeader[] {
  return [
    {
      text: I18n.get(Translations.ENUM_KEY),
      value: 'name',
      align: 'start',
      sortable: true
    },
    {
      text: I18n.get(Translations.ENUM_VALUE),
      value: 'number',
      align: 'start',
      sortable: true
    },
    {
      text: I18n.get(Translations.DISPLAY_NAME),
      value: 'displayName',
      align: 'start',
      sortable: true
    },
    {
      text: '',
      value: 'displayNameKey',
      hidden: true
    }
  ];
}

export function getRows(listFilter?: ListFilter): Rows {
  let documents: Protocol[] = Object.values(persistentDocumentsStore.Protocol);
  if (listFilter) {
    const idList: string[] = Object.keys(listFilter);
    documents = _.orderBy(
      documents.filter((document: Document): boolean =>
        idList.includes(document._id)
      ),
      (document: Document): number => listFilter[document._id],
      'desc'
    );
  }
  return documents.map(
    (document: Protocol): Record<string, string | number | boolean | null> => ({
      _id: document._id,
      name: document.name,
      number: document.number ?? '',
      displayName:
        persistentDocumentsStore.Localization[
          document.displayName?._id ?? ''
        ]?.[userStore.locale],
      displayNameKey:
        persistentDocumentsStore.Localization[document.displayName?._id ?? '']
          ?.stringIdentifier
    })
  );
}

export const sortListBy: string | string[] = 'number';

export async function getCategories(
  document: Partial<Protocol>
): Promise<DetailData> {
  return {
    title: document.name,
    subtitle: document.number?.toString(),
    categories: [
      {
        name: I18n.get(Translations.GENERAL),
        fields: [
          {
            type: FieldTypes.TEXT,
            label: I18n.get(Translations.ENUM_KEY),
            required: true,
            model: 'name',
            value: document.name,
            prefix: 'CANodeProtocol'
          },
          {
            type: FieldTypes.NUMBER,
            label: I18n.get(Translations.ENUM_VALUE),
            required: true,
            model: 'number',
            value: document.number
          },
          {
            type: FieldTypes.AUTOCOMPLETE,
            label: I18n.get(Translations.DISPLAY_NAME),
            required: false,
            model: 'displayName',
            value: document.displayName,
            docType: DocTypes.Localization,
            textField: (item: Document): string =>
              persistentDocumentsStore.Localization[item._id]?.[
                userStore.locale
              ] || 'unknown',
            valueField: '_id',
            additionalText: (item: Document): string =>
              persistentDocumentsStore.Localization[item._id]
                ?.stringIdentifier || 'unknown',
            options: Object.values(
              persistentDocumentsStore.Localization
            ).filter((loc: Localization): boolean =>
              loc.stringIdentifier.toUpperCase().startsWith('GENERAL_PROTOCOL_')
            ),
            multiple: false
          },
          {
            type: FieldTypes.AUTOCOMPLETE,
            label: I18n.get(Translations.CUBES),
            required: true,
            model: 'cubes',
            value: document.cubes,
            docType: DocTypes.Cube,
            textField: (item: Document): string =>
              persistentDocumentsStore.Localization[
                persistentDocumentsStore.Cube[item._id]?.displayName?._id ?? ''
              ]?.[userStore.locale] || 'unknown',
            additionalText: (item: Document): string => {
              const doc: Cube = persistentDocumentsStore.Cube[item._id];
              return `${doc?.name || 'unknown'}, ${doc?.number || -1}`;
            },
            valueField: '_id',
            options: Object.values(persistentDocumentsStore.Cube).sort(
              (item1: Cube, item2: Cube): number => item1.number - item2.number
            ),
            multiple: true
          }
        ]
      }
    ]
  };
}

export function getFilters(): DocsFilter[] {
  return [
    {
      operators: [
        'eq',
        'ne',
        'exists',
        'notExists',
        'contains',
        'notContains',
        'in'
      ],
      label: I18n.get(Translations.ENUM_KEY),
      property: 'name'
    },
    {
      operators: [
        'eq',
        'ne',
        'exists',
        'notExists',
        'le',
        'lt',
        'ge',
        'gt',
        'between',
        'in'
      ],
      label: I18n.get(Translations.ENUM_VALUE),
      validator: (data: string): boolean => !isNaN(parseInt(data)),
      property: 'number'
    },
    {
      operators: ['eq', 'ne', 'exists', 'notExists', 'in'],
      label: I18n.get(Translations.DISPLAY_NAME),
      property: 'displayName',
      textField: (item: Document): string =>
        persistentDocumentsStore.Localization[item._id]?.[userStore.locale] ||
        'unknown',
      valueField: '_id',
      options: Object.values(
        persistentDocumentsStore.Localization
      ).filter((loc: Localization): boolean =>
        loc.stringIdentifier.toUpperCase().startsWith('GENERAL_PROTOCOL_')
      )
    },
    {
      operators: ['all', 'any', 'none', 'exists', 'notExists'],
      label: I18n.get(Translations.CUBES),
      property: 'cubes',
      textField: (item: Document): string => {
        const doc: Cube = persistentDocumentsStore.Cube[item._id];
        return `${doc?.name || 'unknown'}, ${doc?.number || -1}`;
      },
      valueField: '_id',
      options: Object.values(persistentDocumentsStore.Cube).sort(
        (item1: Cube, item2: Cube): number => item1.number - item2.number
      )
    }
  ];
}
